import { api } from "./users";
import {
  bookingAccessParams,
  storeBookingAccess,
} from "../utils/bookingAccess";
import i18n from "../i18n";

// 🔍 Пошук міст (українською/англійською). lang — код мови для відповіді (uk/en).
export const searchCities = async (query, lang = "uk") => {
  const response = await api.get(`/cities/search`, {
    params: { q: query, lang },
  });
  return response.data;
};

// 📌 Топ міст з маршрутів (для підказок у пошуку). role: "from" — звідки, "to" — куди. lang — код мови (uk/en).
export const getTopCities = async (lang = "uk", limit = 5, role = null) => {
  const params = { lang, limit };
  if (role === "from" || role === "to") params.role = role;
  const response = await api.get(`/cities/top/`, { params });
  return Array.isArray(response.data) ? response.data : [];
};

// 🔍 Пошук рейсів. lang — код мови для назв міст у відповіді (uk/en).
// Повертає { direct: Trip[], transfers: TransferItinerary[], partners: [] }.
export const searchTrips = async (from, to, date, adults, children, lang = "uk") => {
  const response = await api.get(`/trips/search`, {
    params: { from, to, date, adults, children, lang },
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return { direct: data, transfers: [], partners: [] };
  }
  return {
    direct: data.direct ?? [],
    transfers: data.transfers ?? [],
    partners: data.partners ?? [],
  };
};

// 🔍 Пошук рейсів ContraBUS (партнери). Показувати після своїх.
export const searchPartnerTrips = async (from, to, date, adults, children) => {
  const response = await api.get(`/network/search/`, {
    params: { from, to, date, adults, children },
  });
  return response.data?.partners ?? [];
};

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const bookPartnerTicket = async (bookingData, { idempotencyKey } = {}) => {
  const key = idempotencyKey || newIdempotencyKey();
  const response = await api.post(`/network/book/`, bookingData, {
    headers: { "Idempotency-Key": key },
  });
  const data = response.data;
  storeBookingAccess(data?.booking_id || data?.booking_number, data?.access_token);
  return data;
};

export const getPartnerRouteInfo = async (dataBundle) => {
  try {
    const response = await api.post(`/network/route-info/`, {
      data_bundle: dataBundle,
    });
    return response.data ?? { schedule: [], carrier: {} };
  } catch (error) {
    return { schedule: [], carrier: {} };
  }
};

export const getTripDetails = async (trip_id) => {
  const response = await api.get(`/trips/${trip_id}`);
  return response.data;
};

// 📌 Бронювання квитків
export const bookTicket = async (bookingData, { idempotencyKey } = {}) => {
  const key = idempotencyKey || newIdempotencyKey();
  const response = await api.post(`/bookings/book_ticket/`, bookingData, {
    headers: { "Idempotency-Key": key },
  });
  const data = response.data;
  storeBookingAccess(data?.booking_id, data?.access_token);
  return data;
};

export const bookItinerary = async (bookingData, { idempotencyKey } = {}) => {
  const key = idempotencyKey || newIdempotencyKey();
  const response = await api.post(`/bookings/book_itinerary/`, bookingData, {
    headers: { "Idempotency-Key": key },
  });
  const data = response.data;
  storeBookingAccess(data?.booking_id, data?.access_token);
  return data;
};

export const bookRoundTrip = async (bookingData, { idempotencyKey } = {}) => {
  const key = idempotencyKey || newIdempotencyKey();
  const response = await api.post(`/bookings/book_round_trip/`, bookingData, {
    headers: { "Idempotency-Key": key },
  });
  const data = response.data;
  storeBookingAccess(data?.booking_id, data?.access_token);
  return data;
};

export const validatePromoCode = async (payload) => {
  const response = await api.post(`/promos/validate/`, payload);
  return response.data;
};

// 💳 Створення платежу в LiqPay
export const createPayment = async (bookingId, { confirmNewPrice = false } = {}) => {
  const response = await api.post(
    `/payments/`,
    {
      booking_id: bookingId,
      confirm_new_price: confirmNewPrice,
      ...bookingAccessParams(bookingId),
    },
    { params: bookingAccessParams(bookingId) }
  );
  return response.data;
};

// 💰 Перевірка актуальної ціни перед оплатою
export const getPaymentQuote = async (bookingId) => {
  const response = await api.get(`/payments/quote/`, {
    params: { booking_id: bookingId, ...bookingAccessParams(bookingId) },
  });
  return response.data;
};

// 🔄 Перевірка статусу бронювання
export const checkBookingStatus = async (bookingId) => {
  const response = await api.get(`/bookings/check-booking-status/${bookingId}/`, {
    params: bookingAccessParams(bookingId),
  });
  return response.data;
};

export const getBookingDetails = async (bookingId) => {
  try {
    const response = await api.get(`/bookings/${bookingId}/`, {
      params: bookingAccessParams(bookingId),
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

/** Path for the shared `api` client (`…/api` base): ignore wrong absolute Host from generate. */
const ticketDownloadPath = (url) => {
  if (!url) return url;
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url;
    const marker = "/bookings/download-ticket/";
    const idx = path.indexOf(marker);
    if (idx !== -1) {
      return path.slice(idx).replace(/\/?$/, "/");
    }
    return path.startsWith("/api/") ? path.slice(4) : path;
  } catch {
    return url;
  }
};

const downloadPdfFromUrl = async (url, bookingId) => {
  const path = ticketDownloadPath(url);
  const ticketResponse = await api.get(path, {
    responseType: "blob",
    params: bookingAccessParams(bookingId),
  });
  const blob = new Blob([ticketResponse.data], { type: "application/pdf" });
  const filename =
    path.split("/").filter(Boolean).pop() || "ticket.pdf";
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadTickets = async (bookingId, legIndex) => {
  try {
    const params = { ...bookingAccessParams(bookingId) };
    if (legIndex) params.leg_index = legIndex;
    const response = await api.get(`/bookings/generate-tickets/${bookingId}/`, {
      params,
    });
    const { tickets } = response.data;

    if (tickets && tickets.length > 0) {
      for (const ticketUrl of tickets) {
        await downloadPdfFromUrl(ticketUrl, bookingId);
      }
    } else {
      alert(i18n.t("apiErrors.tickets"));
    }
  } catch (error) {
    alert(i18n.t("apiErrors.ticketsDownload"));
  }
};

export const downloadPassengerTicket = async (bookingId, passengerId, legIndex) => {
  try {
    const params = { ...bookingAccessParams(bookingId) };
    if (legIndex) params.leg_index = legIndex;
    const response = await api.get(
      `/bookings/${bookingId}/passengers/${passengerId}/generate-ticket/`,
      { params }
    );

    const { ticket } = response.data;

    if (ticket) {
      await downloadPdfFromUrl(ticket, bookingId);
    } else {
      alert(i18n.t("apiErrors.ticket"));
    }
  } catch (error) {
    alert(i18n.t("apiErrors.ticketDownload"));
  }
};

export const validateTicket = async (bookingId, passengerId) => {
  try {
    const response = await api.get(
      `/bookings/validate-ticket/${bookingId}/${passengerId}/`
    );
    return response.data;
  } catch (err) {
    throw err.response?.data?.message || i18n.t("apiErrors.ticketCheck");
  }
};
